/**
 * Industry-specific sample data for onboarding template previews.
 * Mirrors Owl Ops' INDUSTRY_SAMPLE_DATA and INDUSTRY_TO_GROUP mappings.
 */

interface IndustrySampleOverride {
  businessName: string;
  businessEmail: string;
  notes: string;
  lineItems: Array<{ name: string; description: string; quantity: number; rate: number; tax: number; amount: number }>;
  lineItemSections: Array<{ name: string; items: Array<{ name: string; description: string; quantity: number; rate: number; tax: number; amount: number }> }>;
}

const INDUSTRY_SAMPLE_DATA: Record<string, IndustrySampleOverride> = {
  electrical: {
    businessName: 'Pro Electric Services',
    businessEmail: 'info@proelectric.example.com',
    notes: 'Thank you for choosing Pro Electric Services!',
    lineItems: [
      { name: 'Electrical Installation', description: 'Panel upgrade installation labor', quantity: 8, rate: 150, tax: 10, amount: 1200 },
      { name: '200A Electrical Panel', description: 'Square D 200-amp main breaker panel', quantity: 1, rate: 450, tax: 10, amount: 450 },
      { name: 'Copper Wire 6 AWG', description: '6 AWG copper wire, 100ft roll', quantity: 2, rate: 185, tax: 10, amount: 370 },
    ],
    lineItemSections: [
      { name: 'Labor & Services', items: [{ name: 'Electrical Installation', description: 'Panel upgrade installation labor', quantity: 8, rate: 150, tax: 10, amount: 1200 }] },
      { name: 'Parts & Materials', items: [
        { name: '200A Electrical Panel', description: 'Square D 200-amp main breaker panel', quantity: 1, rate: 450, tax: 10, amount: 450 },
        { name: 'Copper Wire 6 AWG', description: '6 AWG copper wire, 100ft roll', quantity: 2, rate: 185, tax: 10, amount: 370 },
      ]},
    ],
  },
  plumbing: {
    businessName: 'Clear Flow Plumbing',
    businessEmail: 'service@clearflow.example.com',
    notes: 'Thank you for choosing Clear Flow Plumbing!',
    lineItems: [
      { name: 'Drain Cleaning Service', description: 'Main line drain cleaning with camera inspection', quantity: 1, rate: 275, tax: 10, amount: 275 },
      { name: 'Water Heater Installation', description: '50 gallon tank water heater installation labor', quantity: 4, rate: 95, tax: 10, amount: 380 },
      { name: 'Rheem 50 Gal Water Heater', description: 'Rheem Performance 50 gallon gas water heater', quantity: 1, rate: 649, tax: 10, amount: 649 },
    ],
    lineItemSections: [
      { name: 'Services', items: [
        { name: 'Drain Cleaning Service', description: 'Main line drain cleaning with camera inspection', quantity: 1, rate: 275, tax: 10, amount: 275 },
        { name: 'Water Heater Installation', description: '50 gallon tank water heater installation labor', quantity: 4, rate: 95, tax: 10, amount: 380 },
      ]},
      { name: 'Equipment', items: [{ name: 'Rheem 50 Gal Water Heater', description: 'Rheem Performance 50 gallon gas water heater', quantity: 1, rate: 649, tax: 10, amount: 649 }] },
    ],
  },
  hvac: {
    businessName: 'Comfort Air HVAC',
    businessEmail: 'service@comfortair.example.com',
    notes: 'Thank you for choosing Comfort Air HVAC!',
    lineItems: [
      { name: 'AC System Diagnosis', description: 'Full system diagnostic and refrigerant check', quantity: 1, rate: 125, tax: 10, amount: 125 },
      { name: 'Refrigerant Recharge', description: 'R-410A refrigerant recharge service', quantity: 3, rate: 85, tax: 10, amount: 255 },
      { name: 'Capacitor Replacement', description: 'Dual run capacitor 45/5 MFD', quantity: 1, rate: 175, tax: 10, amount: 175 },
    ],
    lineItemSections: [
      { name: 'Service Labor', items: [
        { name: 'AC System Diagnosis', description: 'Full system diagnostic and refrigerant check', quantity: 1, rate: 125, tax: 10, amount: 125 },
        { name: 'Refrigerant Recharge', description: 'R-410A refrigerant recharge service', quantity: 3, rate: 85, tax: 10, amount: 255 },
      ]},
      { name: 'Parts', items: [{ name: 'Capacitor Replacement', description: 'Dual run capacitor 45/5 MFD', quantity: 1, rate: 175, tax: 10, amount: 175 }] },
    ],
  },
  construction: {
    businessName: 'Summit Builders',
    businessEmail: 'projects@summitbuilders.example.com',
    notes: 'Thank you for choosing Summit Builders!',
    lineItems: [
      { name: 'Framing Labor', description: 'Structural framing for 400 sq ft addition', quantity: 40, rate: 65, tax: 10, amount: 2600 },
      { name: 'Lumber Package', description: '2x6 SPF lumber and hardware package', quantity: 1, rate: 1850, tax: 10, amount: 1850 },
      { name: 'Permit & Inspection', description: 'Building permit and inspection fees', quantity: 1, rate: 450, tax: 0, amount: 450 },
    ],
    lineItemSections: [
      { name: 'Labor', items: [{ name: 'Framing Labor', description: 'Structural framing for 400 sq ft addition', quantity: 40, rate: 65, tax: 10, amount: 2600 }] },
      { name: 'Materials & Fees', items: [
        { name: 'Lumber Package', description: '2x6 SPF lumber and hardware package', quantity: 1, rate: 1850, tax: 10, amount: 1850 },
        { name: 'Permit & Inspection', description: 'Building permit and inspection fees', quantity: 1, rate: 450, tax: 0, amount: 450 },
      ]},
    ],
  },
  painting: {
    businessName: 'Fresh Coat Painting',
    businessEmail: 'quotes@freshcoat.example.com',
    notes: 'Thank you for choosing Fresh Coat Painting!',
    lineItems: [
      { name: 'Interior Painting', description: 'Living room and dining room walls (800 sq ft)', quantity: 1, rate: 1200, tax: 10, amount: 1200 },
      { name: 'Prep & Primer', description: 'Surface prep, patching, and primer coat', quantity: 1, rate: 350, tax: 10, amount: 350 },
      { name: 'Benjamin Moore Paint', description: 'Regal Select interior paint, 5 gallons', quantity: 5, rate: 75, tax: 10, amount: 375 },
    ],
    lineItemSections: [
      { name: 'Labor', items: [
        { name: 'Interior Painting', description: 'Living room and dining room walls (800 sq ft)', quantity: 1, rate: 1200, tax: 10, amount: 1200 },
        { name: 'Prep & Primer', description: 'Surface prep, patching, and primer coat', quantity: 1, rate: 350, tax: 10, amount: 350 },
      ]},
      { name: 'Materials', items: [{ name: 'Benjamin Moore Paint', description: 'Regal Select interior paint, 5 gallons', quantity: 5, rate: 75, tax: 10, amount: 375 }] },
    ],
  },
  landscaping: {
    businessName: 'Green Valley Landscaping',
    businessEmail: 'info@greenvalley.example.com',
    notes: 'Thank you for choosing Green Valley Landscaping!',
    lineItems: [
      { name: 'Lawn Mowing Service', description: 'Weekly lawn mowing and edging (4 visits)', quantity: 4, rate: 65, tax: 10, amount: 260 },
      { name: 'Shrub Trimming', description: 'Shrub and hedge trimming service', quantity: 1, rate: 185, tax: 10, amount: 185 },
      { name: 'Mulch Installation', description: 'Premium hardwood mulch, 5 yards installed', quantity: 5, rate: 85, tax: 10, amount: 425 },
    ],
    lineItemSections: [
      { name: 'Services', items: [
        { name: 'Lawn Mowing Service', description: 'Weekly lawn mowing and edging (4 visits)', quantity: 4, rate: 65, tax: 10, amount: 260 },
        { name: 'Shrub Trimming', description: 'Shrub and hedge trimming service', quantity: 1, rate: 185, tax: 10, amount: 185 },
      ]},
      { name: 'Materials', items: [{ name: 'Mulch Installation', description: 'Premium hardwood mulch, 5 yards installed', quantity: 5, rate: 85, tax: 10, amount: 425 }] },
    ],
  },
  cleaning: {
    businessName: 'Sparkle Clean Services',
    businessEmail: 'bookings@sparkleclean.example.com',
    notes: 'Thank you for choosing Sparkle Clean Services!',
    lineItems: [
      { name: 'Deep House Cleaning', description: 'Complete deep cleaning service, 2500 sq ft home', quantity: 1, rate: 350, tax: 10, amount: 350 },
      { name: 'Carpet Shampooing', description: 'Professional carpet cleaning, 3 rooms', quantity: 3, rate: 75, tax: 10, amount: 225 },
      { name: 'Window Cleaning', description: 'Interior and exterior window cleaning', quantity: 1, rate: 185, tax: 10, amount: 185 },
    ],
    lineItemSections: [
      { name: 'Cleaning Services', items: [
        { name: 'Deep House Cleaning', description: 'Complete deep cleaning service, 2500 sq ft home', quantity: 1, rate: 350, tax: 10, amount: 350 },
        { name: 'Carpet Shampooing', description: 'Professional carpet cleaning, 3 rooms', quantity: 3, rate: 75, tax: 10, amount: 225 },
        { name: 'Window Cleaning', description: 'Interior and exterior window cleaning', quantity: 1, rate: 185, tax: 10, amount: 185 },
      ]},
    ],
  },
  technology: {
    businessName: 'Digital Solutions LLC',
    businessEmail: 'support@digitalsolutions.example.com',
    notes: 'Thank you for choosing Digital Solutions!',
    lineItems: [
      { name: 'Network Setup', description: 'Small business network configuration and setup', quantity: 8, rate: 125, tax: 10, amount: 1000 },
      { name: 'Workstation Setup', description: 'Computer setup and software installation', quantity: 5, rate: 95, tax: 10, amount: 475 },
      { name: 'Managed IT Support', description: 'Monthly managed IT support (first month)', quantity: 1, rate: 299, tax: 10, amount: 299 },
    ],
    lineItemSections: [
      { name: 'IT Services', items: [
        { name: 'Network Setup', description: 'Small business network configuration and setup', quantity: 8, rate: 125, tax: 10, amount: 1000 },
        { name: 'Workstation Setup', description: 'Computer setup and software installation', quantity: 5, rate: 95, tax: 10, amount: 475 },
        { name: 'Managed IT Support', description: 'Monthly managed IT support (first month)', quantity: 1, rate: 299, tax: 10, amount: 299 },
      ]},
    ],
  },
  automotive: {
    businessName: 'Precision Auto Care',
    businessEmail: 'service@precisionauto.example.com',
    notes: 'Thank you for choosing Precision Auto Care!',
    lineItems: [
      { name: 'Brake Service', description: 'Front brake pad replacement and rotor resurfacing', quantity: 1, rate: 285, tax: 10, amount: 285 },
      { name: 'Oil Change', description: 'Full synthetic oil change with filter', quantity: 1, rate: 75, tax: 10, amount: 75 },
      { name: 'Brake Pads', description: 'Ceramic brake pads, front axle set', quantity: 1, rate: 125, tax: 10, amount: 125 },
    ],
    lineItemSections: [
      { name: 'Labor', items: [
        { name: 'Brake Service', description: 'Front brake pad replacement and rotor resurfacing', quantity: 1, rate: 285, tax: 10, amount: 285 },
        { name: 'Oil Change', description: 'Full synthetic oil change with filter', quantity: 1, rate: 75, tax: 10, amount: 75 },
      ]},
      { name: 'Parts', items: [{ name: 'Brake Pads', description: 'Ceramic brake pads, front axle set', quantity: 1, rate: 125, tax: 10, amount: 125 }] },
    ],
  },
  professional: {
    businessName: 'Sterling Consulting Group',
    businessEmail: 'contact@sterlingconsulting.example.com',
    notes: 'Thank you for choosing Sterling Consulting!',
    lineItems: [
      { name: 'Strategic Consulting', description: 'Business strategy consulting session', quantity: 8, rate: 250, tax: 10, amount: 2000 },
      { name: 'Market Analysis', description: 'Comprehensive market research report', quantity: 1, rate: 1500, tax: 10, amount: 1500 },
      { name: 'Implementation Support', description: 'Strategy implementation guidance', quantity: 4, rate: 200, tax: 10, amount: 800 },
    ],
    lineItemSections: [
      { name: 'Consulting Services', items: [
        { name: 'Strategic Consulting', description: 'Business strategy consulting session', quantity: 8, rate: 250, tax: 10, amount: 2000 },
        { name: 'Market Analysis', description: 'Comprehensive market research report', quantity: 1, rate: 1500, tax: 10, amount: 1500 },
        { name: 'Implementation Support', description: 'Strategy implementation guidance', quantity: 4, rate: 200, tax: 10, amount: 800 },
      ]},
    ],
  },
  creative: {
    businessName: 'Pixel Perfect Design',
    businessEmail: 'hello@pixelperfect.example.com',
    notes: 'Thank you for choosing Pixel Perfect Design!',
    lineItems: [
      { name: 'Logo Design', description: 'Custom logo design with 3 concepts', quantity: 1, rate: 850, tax: 10, amount: 850 },
      { name: 'Brand Guidelines', description: 'Complete brand style guide document', quantity: 1, rate: 650, tax: 10, amount: 650 },
      { name: 'Business Cards', description: 'Business card design, print-ready files', quantity: 1, rate: 250, tax: 10, amount: 250 },
    ],
    lineItemSections: [
      { name: 'Design Services', items: [
        { name: 'Logo Design', description: 'Custom logo design with 3 concepts', quantity: 1, rate: 850, tax: 10, amount: 850 },
        { name: 'Brand Guidelines', description: 'Complete brand style guide document', quantity: 1, rate: 650, tax: 10, amount: 650 },
        { name: 'Business Cards', description: 'Business card design, print-ready files', quantity: 1, rate: 250, tax: 10, amount: 250 },
      ]},
    ],
  },
  beauty: {
    businessName: 'Luxe Beauty Studio',
    businessEmail: 'appointments@luxebeauty.example.com',
    notes: 'Thank you for visiting Luxe Beauty Studio!',
    lineItems: [
      { name: 'Haircut & Style', description: "Women's haircut with blowout styling", quantity: 1, rate: 85, tax: 10, amount: 85 },
      { name: 'Color Treatment', description: 'Full color with highlights', quantity: 1, rate: 175, tax: 10, amount: 175 },
      { name: 'Deep Conditioning', description: 'Keratin deep conditioning treatment', quantity: 1, rate: 45, tax: 10, amount: 45 },
    ],
    lineItemSections: [
      { name: 'Salon Services', items: [
        { name: 'Haircut & Style', description: "Women's haircut with blowout styling", quantity: 1, rate: 85, tax: 10, amount: 85 },
        { name: 'Color Treatment', description: 'Full color with highlights', quantity: 1, rate: 175, tax: 10, amount: 175 },
        { name: 'Deep Conditioning', description: 'Keratin deep conditioning treatment', quantity: 1, rate: 45, tax: 10, amount: 45 },
      ]},
    ],
  },
  pet_services: {
    businessName: 'Happy Paws Pet Care',
    businessEmail: 'woof@happypaws.example.com',
    notes: 'Thank you for trusting Happy Paws with your furry friend!',
    lineItems: [
      { name: 'Full Grooming Package', description: 'Bath, haircut, nail trim, ear cleaning', quantity: 1, rate: 75, tax: 10, amount: 75 },
      { name: 'Dog Walking (Weekly)', description: '30-minute daily walks, 5 days', quantity: 5, rate: 25, tax: 10, amount: 125 },
      { name: 'Pet Sitting', description: 'In-home pet sitting, overnight stay', quantity: 2, rate: 65, tax: 10, amount: 130 },
    ],
    lineItemSections: [
      { name: 'Pet Services', items: [
        { name: 'Full Grooming Package', description: 'Bath, haircut, nail trim, ear cleaning', quantity: 1, rate: 75, tax: 10, amount: 75 },
        { name: 'Dog Walking (Weekly)', description: '30-minute daily walks, 5 days', quantity: 5, rate: 25, tax: 10, amount: 125 },
        { name: 'Pet Sitting', description: 'In-home pet sitting, overnight stay', quantity: 2, rate: 65, tax: 10, amount: 130 },
      ]},
    ],
  },
  events: {
    businessName: 'Stellar Events Co.',
    businessEmail: 'events@stellarevents.example.com',
    notes: 'Thank you for letting us make your event special!',
    lineItems: [
      { name: 'Event Coordination', description: 'Full day-of event coordination services', quantity: 1, rate: 1200, tax: 10, amount: 1200 },
      { name: 'Venue Setup', description: 'Venue decoration and setup', quantity: 1, rate: 650, tax: 10, amount: 650 },
      { name: 'Floral Arrangements', description: 'Table centerpieces and bouquets', quantity: 1, rate: 450, tax: 10, amount: 450 },
    ],
    lineItemSections: [
      { name: 'Event Services', items: [
        { name: 'Event Coordination', description: 'Full day-of event coordination services', quantity: 1, rate: 1200, tax: 10, amount: 1200 },
        { name: 'Venue Setup', description: 'Venue decoration and setup', quantity: 1, rate: 650, tax: 10, amount: 650 },
      ]},
      { name: 'Supplies', items: [{ name: 'Floral Arrangements', description: 'Table centerpieces and bouquets', quantity: 1, rate: 450, tax: 10, amount: 450 }] },
    ],
  },
  moving: {
    businessName: 'Swift Move Logistics',
    businessEmail: 'moves@swiftmove.example.com',
    notes: 'Thank you for moving with Swift Move Logistics!',
    lineItems: [
      { name: 'Moving Labor', description: '2 movers, 4 hours local move', quantity: 4, rate: 150, tax: 10, amount: 600 },
      { name: 'Packing Service', description: 'Professional packing of 2 bedroom home', quantity: 1, rate: 350, tax: 10, amount: 350 },
      { name: 'Moving Supplies', description: 'Boxes, tape, packing paper bundle', quantity: 1, rate: 125, tax: 10, amount: 125 },
    ],
    lineItemSections: [
      { name: 'Moving Services', items: [
        { name: 'Moving Labor', description: '2 movers, 4 hours local move', quantity: 4, rate: 150, tax: 10, amount: 600 },
        { name: 'Packing Service', description: 'Professional packing of 2 bedroom home', quantity: 1, rate: 350, tax: 10, amount: 350 },
      ]},
      { name: 'Supplies', items: [{ name: 'Moving Supplies', description: 'Boxes, tape, packing paper bundle', quantity: 1, rate: 125, tax: 10, amount: 125 }] },
    ],
  },
};

const INDUSTRY_TO_GROUP: Record<string, string> = {
  electrical: 'electrical', solar: 'electrical', generator: 'electrical', lighting: 'electrical',
  plumbing: 'plumbing', drain_cleaning: 'plumbing', septic: 'plumbing', irrigation: 'plumbing',
  hvac: 'hvac', refrigeration: 'hvac',
  general_contractor: 'construction', subcontractor: 'construction', carpentry: 'construction', roofing: 'construction', siding: 'construction', masonry: 'construction', concrete: 'construction', demolition: 'construction', excavation: 'construction', drywall: 'construction', insulation: 'construction', fencing: 'construction', decks: 'construction', welding: 'construction', remodeling: 'construction', handyman: 'construction', garage_doors: 'construction', doors_windows: 'construction', cabinets: 'construction', glass_glazing: 'construction', restoration: 'construction', locksmith: 'construction', chimney: 'construction', manufacturing: 'construction', fabrication: 'construction', woodworking: 'construction', upholstery: 'construction',
  painting: 'painting', flooring: 'painting', tile: 'painting',
  landscaping: 'landscaping', lawn_care: 'landscaping', tree_service: 'landscaping', snow_removal: 'landscaping', pool_service: 'landscaping', farming: 'landscaping', ranching: 'landscaping', nursery: 'landscaping',
  cleaning: 'cleaning', house_cleaning: 'cleaning', carpet_cleaning: 'cleaning', window_cleaning: 'cleaning', pressure_washing: 'cleaning', janitorial: 'cleaning', dry_cleaning: 'cleaning', organizing: 'cleaning', pest_control: 'cleaning', tailor: 'cleaning', errand_services: 'cleaning',
  it_consulting: 'technology', software_development: 'technology', web_development: 'technology', web_design: 'technology', computer_repair: 'technology', security_systems: 'technology', home_automation: 'technology', networking: 'technology', electronics_repair: 'technology', appliance_repair: 'technology',
  auto_repair: 'automotive', auto_detailing: 'automotive', towing: 'automotive', car_rental: 'automotive', boat_repair: 'automotive', marine_services: 'automotive',
  consulting: 'professional', accounting: 'professional', bookkeeping: 'professional', attorney: 'professional', notary: 'professional', architecture: 'professional', engineering: 'professional', inspection: 'professional', property_mgmt: 'professional', real_estate: 'professional', interior_design: 'professional', tax_services: 'professional', financial_planning: 'professional', insurance: 'professional', freelancer: 'professional', self_employed: 'professional', sole_trader: 'professional', contractor: 'professional', small_business: 'professional', non_profit: 'professional', agency: 'professional', tutoring: 'professional', therapy: 'professional', chiropractic: 'professional', life_coaching: 'professional', personal_assistant: 'professional', daycare: 'professional', babysitting: 'professional', nanny: 'professional', music_lessons: 'professional', driving_lessons: 'professional', sports_coaching: 'professional', ecommerce: 'professional', retail: 'professional', wholesale: 'professional', rental_services: 'professional', home_health: 'professional', medical: 'professional', dental: 'professional',
  graphic_design: 'creative', logo_design: 'creative', photography: 'creative', videography: 'creative', video_production: 'creative', animation: 'creative', sign_making: 'creative', marketing: 'creative', social_media: 'creative', seo: 'creative', advertising: 'creative', copywriting: 'creative', freelance_writing: 'creative', editing: 'creative', translation: 'creative', transcription: 'creative', jewelry_repair: 'creative',
  hair_salon: 'beauty', barbershop: 'beauty', nail_salon: 'beauty', makeup_artist: 'beauty', esthetician: 'beauty', lash_extensions: 'beauty', tattoo: 'beauty', spa: 'beauty', massage: 'beauty', personal_training: 'beauty', yoga: 'beauty', nutrition: 'beauty',
  dog_walking: 'pet_services', pet_sitting: 'pet_services', pet_grooming: 'pet_services', dog_training: 'pet_services', veterinary: 'pet_services',
  event_planning: 'events', catering: 'events', dj_services: 'events', florist: 'events', bartending: 'events', musician: 'events', party_rentals: 'events', bakery: 'events', food_truck: 'events', personal_chef: 'events', restaurant: 'events', hospitality: 'events', vacation_rental: 'events', airbnb_mgmt: 'events',
  moving: 'moving', junk_removal: 'moving', hauling: 'moving', delivery: 'moving', courier: 'moving', freight: 'moving', trucking: 'moving', shipping: 'moving',
};

/**
 * Get industry sample data override for a given industry ID.
 * Returns undefined if no specific data exists (falls back to default).
 */
export function getIndustrySampleData(industryId: string): IndustrySampleOverride | undefined {
  const group = INDUSTRY_TO_GROUP[industryId];
  if (!group) return undefined;
  return INDUSTRY_SAMPLE_DATA[group];
}

/**
 * Apply industry overrides to Owl Ops API data object (mutates in place).
 * Call this after buildOwlOpsData() and before sending to renderHtml().
 */
export function applyIndustryOverrides(
  apiData: Record<string, unknown>,
  industryId: string | undefined,
  userBusinessName?: string,
  userAddress?: string,
): void {
  if (!industryId) return;
  const override = getIndustrySampleData(industryId);
  if (!override) return;

  const doc = apiData.document as Record<string, unknown> | undefined;
  const biz = apiData.business as Record<string, unknown> | undefined;

  if (biz) {
    // Use user's name/address if provided, otherwise use industry sample
    if (!userBusinessName) biz.name = override.businessName;
    biz.email = override.businessEmail;
  }

  if (doc) {
    doc.notes = override.notes;

    // Override line items
    doc.line_items = override.lineItems.map((item) => ({
      name: item.name,
      description: item.description,
      quantity: item.quantity,
      rate: item.rate,
      tax: item.tax,
      amount: item.amount,
    }));

    doc.line_item_sections = override.lineItemSections.map((section) => ({
      name: section.name,
      items: section.items.map((item) => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        rate: item.rate,
        tax: item.tax,
        amount: item.amount,
      })),
    }));

    // Recalculate totals
    const subtotal = override.lineItems.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = override.lineItems.reduce((sum, item) => sum + (item.amount * item.tax / 100), 0);
    const total = subtotal + taxAmount;

    doc.totals = [
      { key: 'subtotal', label: 'Subtotal', value: subtotal, style: 'default' },
      { key: 'tax', label: 'Tax', value: Math.round(taxAmount * 100) / 100, style: 'default' },
      { key: 'total', label: 'Total', value: Math.round(total * 100) / 100, style: 'emphasized', divider: 'above' },
    ];
  }
}
